/**
 * Workspace layout — the persistent chrome for every `(workspace)/*` page.
 *
 * This pathless layout route renders the unified Topbar **once** and an
 * `<Outlet/>` for the page content. Because the layout sits above the
 * outlet in the route tree, navigating between workspace pages swaps only
 * the outlet — the chrome never unmounts.
 *
 * Layout (top → bottom):
 *   1. Topbar (brand → launcher, module switcher, section tabs, search,
 *      Ask Flint + Inbox, theme, avatar) — ADR-0038; the sidebar rail is
 *      retired, cross-module navigation lives in the switcher + /launch.
 *      A page can suppress it entirely with `WorkspaceShell.hideTopbar` when
 *      its module owns the full chrome (the Store admin's own rail).
 *   2. TrialBanner (when the subscription warrants it) — deliberately NOT
 *      suppressed with the topbar; it's a payment-state warning.
 *   3. <Outlet/> (page content, full width)
 */
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Topbar } from "../../features/workspace/index.ts";
import { TrialBanner } from "../../features/workspace/components/trial-banner.tsx";
import { useWorkspaceChrome } from "../../features/workspace/lib/use-workspace-chrome.ts";
import { meQueryOptions } from "../../lib/session.ts";

export const Route = createFileRoute("/(workspace)")({
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const me = useQuery(meQueryOptions);
  const hideTopbar = useWorkspaceChrome((s) => s.hideTopbar);

  const modules = me.data?.company?.modules ?? [];
  const permissions = me.data?.company?.permissions ?? [];
  const subscription = me.data?.company?.subscription ?? null;

  return (
    <div className="flex h-screen min-w-0 flex-col overflow-hidden bg-bg">
      {!hideTopbar && <Topbar modules={modules} permissions={permissions} />}
      {subscription && (
        <TrialBanner
          plan={subscription.plan}
          status={subscription.status}
          trialDaysLeft={subscription.trialDaysLeft}
          writeEnabled={subscription.writeEnabled}
        />
      )}
      <Outlet />
    </div>
  );
}
